import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList, Legend,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from "recharts";
import { ChartBar, ArrowsClockwise, Trophy, Warning, FilePdf, Scales, GitDiff, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, postForJsonSSE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { exportReportPDF } from "@/lib/pdf";

const PROC_COLORS = {
  "Arc Spray": "#FF9F0A",
  "Flame Spray": "#FF453A",
  "HVOF": "#FF3B30",
  "HVOLF": "#FF2D55",
  "Plasma Spray": "#BF5AF2",
  "Cold Spray": "#0A84FF",
  "Suspension Plasma Spray": "#64D2FF",
};

export const PROPERTY_METRICS = [
  { key: "hardness", label: "Microhardness", unit: "HV", better: "higher" },
  { key: "porosity", label: "Porosity", unit: "%", better: "lower" },
  { key: "bond_strength", label: "Bond Strength", unit: "MPa", better: "higher" },
  { key: "deposition_efficiency", label: "Deposition Efficiency", unit: "%", better: "higher" },
  { key: "oxide_content", label: "Oxide Content", unit: "%", better: "lower" },
  { key: "surface_roughness", label: "Surface Roughness", unit: "Ra µm", better: "lower" },
];

export const ECONOMIC_METRICS = [
  { key: "deposition_rate", label: "Deposition Rate", unit: "kg/h", better: "higher" },
  { key: "relative_cost", label: "Relative Cost", unit: "1–5", better: "lower" },
];

export const ALL_METRICS = [...PROPERTY_METRICS, ...ECONOMIC_METRICS];

const short = (p) => p.replace("Suspension Plasma Spray", "SPS").replace(" Spray", "");

const tooltipStyle = { background: "#121212", border: "1px solid #27272A", borderRadius: 2, fontFamily: "IBM Plex Mono", fontSize: 12 };

export function MetricChart({ metric, data }) {
  const rows = data.map((d) => ({ process: d.process, value: Number(d[metric.key]) || 0, short: short(d.process) }));
  return (
    <div className="border border-border bg-card p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-heading font-bold text-white text-sm">{metric.label}</h3>
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{metric.unit} · {metric.better} = better</span>
      </div>
      <ResponsiveContainer width="100%" height={210}>
        <BarChart data={rows} margin={{ top: 16, right: 8, left: -8, bottom: 0 }}>
          <XAxis dataKey="short" tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#27272A" }} tickLine={false} />
          <YAxis tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#27272A" }} tickLine={false} width={40} />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={tooltipStyle} labelStyle={{ color: "#fff" }} formatter={(v) => [`${v} ${metric.unit}`, metric.label]} />
          <Bar dataKey="value" radius={[2, 2, 0, 0]}>
            <LabelList dataKey="value" position="top" fill="#fff" fontSize={10} fontFamily="IBM Plex Mono" />
            {rows.map((r, i) => <Cell key={i} fill={PROC_COLORS[r.process] || "#0A84FF"} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function RadarOverview({ data }) {
  const procs = data.map((d) => d.process);
  const radarData = PROPERTY_METRICS.map((m) => {
    const vals = data.map((d) => Number(d[m.key]) || 0);
    const min = Math.min(...vals), max = Math.max(...vals);
    const norm = (v) => (max === min ? 50 : (m.better === "higher" ? (v - min) / (max - min) : (max - v) / (max - min)) * 100);
    const row = { metric: m.label };
    data.forEach((d) => { row[short(d.process)] = Math.round(norm(Number(d[m.key]) || 0)); });
    return row;
  });
  return (
    <div className="border border-border bg-card p-4">
      <h3 className="font-heading font-bold text-white text-sm mb-1">Property Radar (normalised)</h3>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Outer = best in class across shown processes</p>
      <ResponsiveContainer width="100%" height={340}>
        <RadarChart data={radarData} outerRadius="72%">
          <PolarGrid stroke="#27272A" />
          <PolarAngleAxis dataKey="metric" tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "IBM Plex Mono" }} />
          <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
          {procs.map((p) => (
            <Radar key={p} name={short(p)} dataKey={short(p)} stroke={PROC_COLORS[p] || "#0A84FF"} fill={PROC_COLORS[p] || "#0A84FF"} fillOpacity={0.12} strokeWidth={2} />
          ))}
          <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} />
          <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "#fff" }} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function CostBreakdown({ data }) {
  const rows = data.map((d) => ({
    short: short(d.process),
    gas: Number(d.cost_breakdown?.gas) || 0,
    powder: Number(d.cost_breakdown?.powder) || 0,
    equipment: Number(d.cost_breakdown?.equipment) || 0,
  }));
  return (
    <div className="border border-border bg-card p-4">
      <h3 className="font-heading font-bold text-white text-sm mb-1">Cost Breakdown</h3>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Relative cost drivers (1–5) · gas · powder · equipment</p>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={rows} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
          <XAxis dataKey="short" tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#27272A" }} tickLine={false} />
          <YAxis tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#27272A" }} tickLine={false} width={30} />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={tooltipStyle} labelStyle={{ color: "#fff" }} />
          <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} />
          <Bar dataKey="gas" stackId="c" fill="#0A84FF" name="Gas / Fuel" radius={[0, 0, 0, 0]} />
          <Bar dataKey="powder" stackId="c" fill="#FF9F0A" name="Powder" />
          <Bar dataKey="equipment" stackId="c" fill="#BF5AF2" name="Equipment" radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function VersusChart({ metric, mats }) {
  const rows = [{
    name: metric.label,
    [mats[0].name]: Number(mats[0][metric.key]) || 0,
    [mats[1].name]: Number(mats[1][metric.key]) || 0,
  }];
  return (
    <div className="border border-border bg-card p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-heading font-bold text-white text-sm">{metric.label}</h3>
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{metric.unit} · {metric.better} = better</span>
      </div>
      <ResponsiveContainer width="100%" height={190}>
        <BarChart data={rows} margin={{ top: 16, right: 8, left: -8, bottom: 0 }}>
          <XAxis dataKey="name" tick={false} axisLine={{ stroke: "#27272A" }} tickLine={false} height={4} />
          <YAxis tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#27272A" }} tickLine={false} width={40} />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={tooltipStyle} labelStyle={{ color: "#fff" }} />
          <Bar dataKey={mats[0].name} fill="#FF3B30" radius={[2, 2, 0, 0]}><LabelList position="top" fill="#fff" fontSize={10} fontFamily="IBM Plex Mono" /></Bar>
          <Bar dataKey={mats[1].name} fill="#0A84FF" radius={[2, 2, 0, 0]}><LabelList position="top" fill="#fff" fontSize={10} fontFamily="IBM Plex Mono" /></Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function Compare() {
  const { checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState("processes");
  const [names, setNames] = useState([]);
  const [material, setMaterial] = useState("");
  const [matA, setMatA] = useState("");
  const [matB, setMatB] = useState("");
  const [result, setResult] = useState(null);
  const [result2, setResult2] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/materials").then((r) => setNames(r.data.map((m) => m.name))).catch(() => {});
  }, []);

  useEffect(() => {
    const preset = searchParams.get("material");
    if (preset) { setMaterial(preset); setMode("processes"); run(preset); }
    // eslint-disable-next-line
  }, [searchParams]);

  const run = async (mat) => {
    const target = typeof mat === "string" ? mat : material;
    if (!target.trim() || loading) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const data = await postForJsonSSE("/compare", { material: target });
      setResult(data);
      checkAuth();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Comparison failed");
    } finally { setLoading(false); }
  };

  const runVersus = async () => {
    if (!matA.trim() || !matB.trim() || loading) return;
    setLoading(true); setError(""); setResult2(null);
    try {
      const data = await postForJsonSSE("/compare-materials", { material_a: matA, material_b: matB });
      setResult2(data);
      checkAuth();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Comparison failed");
    } finally { setLoading(false); }
  };

  const exportPdf = () => {
    if (!result) return;
    const head = ALL_METRICS.map((m) => `<th>${m.label} (${m.unit})</th>`).join("");
    const rows = result.data.map((d) => `<tr><td>${d.process}</td>${ALL_METRICS.map((m) => `<td>${d[m.key] ?? "—"}</td>`).join("")}</tr>`).join("");
    const cost = result.data.map((d) => `<tr><td>${d.process}</td><td>${d.cost_breakdown?.gas ?? "—"}</td><td>${d.cost_breakdown?.powder ?? "—"}</td><td>${d.cost_breakdown?.equipment ?? "—"}</td></tr>`).join("");
    const body = `
      <h2>Process Comparison — ${result.material}</h2>
      <p><strong>Material class:</strong> ${result.material_class || "—"}</p>
      <table><tr><th>Process</th>${head}</tr>${rows}</table>
      <h2>Cost Breakdown (1–5)</h2>
      <table><tr><th>Process</th><th>Gas/Fuel</th><th>Powder</th><th>Equipment</th></tr>${cost}</table>
      <h2>Recommendation</h2>
      <p><strong>${result.best_process || ""}</strong> — ${result.summary || ""}</p>`;
    exportReportPDF({ title: `${result.material} — Process Comparison`, subtitle: "Process Comparison Datasheet", bodyHTML: body });
  };

  const exportVersusPdf = () => {
    if (!result2) return;
    const [a, b] = result2.materials;
    const rows = ALL_METRICS.map((m) => `<tr><td>${m.label} (${m.unit})</td><td>${a[m.key] ?? "—"}</td><td>${b[m.key] ?? "—"}</td></tr>`).join("");
    const body = `
      <h2>${a.name} vs ${b.name}</h2>
      <table><tr><th>Metric</th><th>${a.name} (${a.best_process})</th><th>${b.name} (${b.best_process})</th></tr>${rows}</table>
      <h2>Verdict</h2><p>${result2.verdict || ""}</p>`;
    exportReportPDF({ title: `${a.name} vs ${b.name}`, subtitle: "Material Head-to-Head", bodyHTML: body });
  };

  const inputCls = "rounded-sm bg-card border-border font-mono text-sm focus-visible:ring-primary";

  const saveComparison = async (kind) => {
    try {
      if (kind === "processes") {
        await api.post("/comparisons", { kind, title: `${result.material} — processes`, payload: result });
      } else {
        await api.post("/comparisons", { kind, title: `${result2.materials[0].name} vs ${result2.materials[1].name}`, payload: result2 });
      }
      toast.success("Comparison saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="grain mx-auto max-w-[1400px] px-4 sm:px-6 py-8">
      <div className="flex items-center gap-3 mb-6">
        <ChartBar size={26} weight="bold" className="text-primary" />
        <div>
          <h1 className="font-heading font-black text-white text-2xl">Comparison</h1>
          <p className="text-sm text-muted-foreground">Compare all coating properties &amp; process economics for one material, or two materials head-to-head.</p>
        </div>
      </div>

      {/* Mode tabs */}
      <div className="inline-grid grid-cols-2 border border-border mb-5">
        <button data-testid="mode-processes-btn" onClick={() => { setMode("processes"); setError(""); }}
          className={`flex items-center gap-2 px-4 py-2 text-xs font-mono uppercase tracking-wide transition-colors ${mode === "processes" ? "bg-primary text-white" : "text-muted-foreground hover:text-white"}`}>
          <GitDiff size={15} weight="bold" /> Processes (1 material)
        </button>
        <button data-testid="mode-materials-btn" onClick={() => { setMode("materials"); setError(""); }}
          className={`flex items-center gap-2 px-4 py-2 text-xs font-mono uppercase tracking-wide transition-colors ${mode === "materials" ? "bg-secondary text-white" : "text-muted-foreground hover:text-white"}`}>
          <Scales size={15} weight="bold" /> Material vs Material
        </button>
      </div>

      <datalist id="material-names">{names.map((n) => <option key={n} value={n} />)}</datalist>

      {mode === "processes" ? (
        <div className="flex flex-col sm:flex-row gap-3 mb-6 max-w-2xl">
          <Input data-testid="compare-input" value={material} onChange={(e) => setMaterial(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} placeholder="e.g. WC-Co 88/12, Alumina (Al2O3), Inconel 625" list="material-names" className={`flex-1 ${inputCls}`} />
          <Button data-testid="compare-btn" onClick={run} disabled={loading || !material.trim()} className="rounded-sm bg-primary hover:bg-primary/90 text-white font-mono uppercase tracking-widest text-xs h-10 px-6">
            {loading ? <><ArrowsClockwise size={16} className="animate-spin mr-2" /> Analysing</> : "Compare"}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-3 mb-6 max-w-3xl">
          <Input data-testid="compare-input-a" value={matA} onChange={(e) => setMatA(e.target.value)} placeholder="Material A — e.g. WC-Co 88/12" list="material-names" className={`flex-1 ${inputCls}`} />
          <span className="hidden sm:flex items-center font-mono text-xs text-muted-foreground">vs</span>
          <Input data-testid="compare-input-b" value={matB} onChange={(e) => setMatB(e.target.value)} placeholder="Material B — e.g. Cr3C2-NiCr 75/25" list="material-names" className={`flex-1 ${inputCls}`} />
          <Button data-testid="compare-versus-btn" onClick={runVersus} disabled={loading || !matA.trim() || !matB.trim()} className="rounded-sm bg-secondary hover:bg-secondary/90 text-white font-mono uppercase tracking-widest text-xs h-10 px-6">
            {loading ? <><ArrowsClockwise size={16} className="animate-spin mr-2" /> Analysing</> : "Compare"}
          </Button>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 border border-destructive/50 bg-destructive/10 p-3 rounded-sm mb-4 max-w-2xl">
          <Warning size={18} className="text-destructive" /><span className="text-sm text-destructive">{error}</span>
        </div>
      )}
      {loading && (
        <div className="font-mono text-sm text-muted-foreground">computing comparison matrix<span className="cursor-blink">_</span></div>
      )}

      {/* PROCESSES RESULT */}
      {mode === "processes" && result && !loading && (
        <div data-testid="compare-result">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="font-heading font-bold text-white text-lg">{result.material}</span>
            {result.material_class && <span className="border border-border bg-muted px-2 py-0.5 text-[10px] font-mono uppercase text-muted-foreground rounded-sm">{result.material_class}</span>}
            <Button data-testid="save-comparison-btn" onClick={() => saveComparison("processes")} size="sm" variant="ghost" className="ml-auto rounded-sm h-8 text-xs font-mono uppercase tracking-wide text-primary hover:text-primary hover:bg-primary/10">
              <FloppyDisk size={14} className="mr-1" /> Save
            </Button>
            <Button data-testid="compare-pdf-btn" onClick={exportPdf} size="sm" variant="ghost" className="rounded-sm h-8 text-xs font-mono uppercase tracking-wide text-secondary hover:text-secondary hover:bg-secondary/10">
              <FilePdf size={14} className="mr-1" /> PDF
            </Button>
          </div>

          <h2 className="font-heading font-bold text-white text-sm uppercase tracking-widest mb-3 flex items-center gap-2"><span className="h-3 w-1 bg-primary inline-block" /> Overview</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
            <RadarOverview data={result.data} />
            <CostBreakdown data={result.data} />
          </div>

          <h2 className="font-heading font-bold text-white text-sm uppercase tracking-widest mb-3 flex items-center gap-2"><span className="h-3 w-1 bg-primary inline-block" /> Coating Properties</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {PROPERTY_METRICS.map((m) => <MetricChart key={m.key} metric={m} data={result.data} />)}
          </div>

          <h2 className="font-heading font-bold text-white text-sm uppercase tracking-widest mb-3 flex items-center gap-2"><span className="h-3 w-1 bg-secondary inline-block" /> Process Economics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
            {ECONOMIC_METRICS.map((m) => <MetricChart key={m.key} metric={m} data={result.data} />)}
          </div>

          {result.best_process && (
            <div className="flex items-start gap-3 border border-primary/40 bg-primary/10 p-4 rounded-sm">
              <Trophy size={22} weight="fill" className="text-primary shrink-0 mt-0.5" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-0.5">Recommended · {result.best_process}</div>
                <p className="text-sm text-foreground leading-relaxed">{result.summary}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* MATERIALS RESULT */}
      {mode === "materials" && result2 && !loading && (
        <div data-testid="compare-versus-result">
          <div className="flex flex-wrap items-center gap-4 mb-5">
            {result2.materials.map((m, i) => (
              <div key={m.name} className="flex items-center gap-2">
                <span className={`h-3 w-3 rounded-sm ${i === 0 ? "bg-primary" : "bg-secondary"}`} />
                <span className="font-heading font-bold text-white text-base">{m.name}</span>
                <span className="border border-border bg-muted px-2 py-0.5 text-[10px] font-mono uppercase text-muted-foreground rounded-sm">{m.best_process}</span>
              </div>
            ))}
            <Button data-testid="save-versus-btn" onClick={() => saveComparison("materials")} size="sm" variant="ghost" className="ml-auto rounded-sm h-8 text-xs font-mono uppercase tracking-wide text-primary hover:text-primary hover:bg-primary/10">
              <FloppyDisk size={14} className="mr-1" /> Save
            </Button>
            <Button data-testid="compare-versus-pdf-btn" onClick={exportVersusPdf} size="sm" variant="ghost" className="rounded-sm h-8 text-xs font-mono uppercase tracking-wide text-secondary hover:text-secondary hover:bg-secondary/10">
              <FilePdf size={14} className="mr-1" /> PDF
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            {ALL_METRICS.map((m) => <VersusChart key={m.key} metric={m} mats={result2.materials} />)}
          </div>
          {result2.verdict && (
            <div className="flex items-start gap-3 border border-secondary/40 bg-secondary/10 p-4 rounded-sm">
              <Trophy size={22} weight="fill" className="text-secondary shrink-0 mt-0.5" />
              <p className="text-sm text-foreground leading-relaxed">{result2.verdict}</p>
            </div>
          )}
        </div>
      )}

      {/* Empty states */}
      {mode === "processes" && !result && !loading && !error && (
        <div className="border border-border bg-card p-12 text-center max-w-2xl">
          <ChartBar size={40} className="text-muted-foreground mx-auto mb-3" />
          <p className="font-heading text-white text-lg">Pick a material to compare</p>
          <p className="text-sm text-muted-foreground mt-1">Radar overview, cost breakdown and per-property charts across every applicable process.</p>
        </div>
      )}
      {mode === "materials" && !result2 && !loading && !error && (
        <div className="border border-border bg-card p-12 text-center max-w-2xl">
          <Scales size={40} className="text-muted-foreground mx-auto mb-3" />
          <p className="font-heading text-white text-lg">Compare two feedstocks head-to-head</p>
          <p className="text-sm text-muted-foreground mt-1">Enter two materials to see their best-process properties side by side with a verdict.</p>
        </div>
      )}
    </div>
  );
}
